/* eslint-disable eqeqeq */
import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Form,
  Tab,
  Nav,
} from "react-bootstrap";
import { faCog, faFileText, faNetworkWired } from "@fortawesome/free-solid-svg-icons";
import { Formik, validateYupSchema, yupToFormErrors } from "formik";
import axios from "axios";
import { parse } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NotyfContext from "../contexts/NotyfContext.js";
import useAppDispatch from "../hooks/useAppDispatch.js";
import useLayout from "../hooks/useLayout.js";
import { eventList } from "../redux/slices/events.js";
import { createImportValidationReport, generateSixDigitCode, getImportValidationSummary, getImportValidationToast, isFieldVisible, normalizeRfidTag, normalizeServerImportValidationError, textToArray, validateEmailDomain } from "../assets/functions.js";
import { schemaEventMode02 } from "./Json.js";
import { domainConfig } from "../config.js";
import {
  ZPLModelForm,
  CredentialsComponent,
  SideButtonPanel,
  AppDefinitions,
  RegisterDefinitions,
  EventDefinitions,
  AdvancedSettingsDefinitions,
  SymplaComponent,
} from "./EventsComponents02.js";
import {
  FormType,
  SocialMediaType,
  handleDelete,
  EmailLabelModal,
  hydrateFormFieldVacancies,
} from "./EventsComponents";
import { ExtraSectionsForm } from "./EventsComponentsTabs.js";
import { faReact } from "@fortawesome/free-brands-svg-icons";
import { getErrorPaths, scrollToFirstError } from "../utils/formErrorNavigation.js";
import {
  hasRegisterTemplateForType,
  normalizeRegisterTemplateConfiguration,
  REGISTRATION_EMAIL_USER_TYPES,
} from "../utils/registrationEmailTemplates.js";
import { getWhatsAppRegistrationField, getWhatsAppRegistrationError, WHATSAPP_REGISTRATION_USER_TYPES } from "../utils/whatsappRegistration.js";

const FormikValuesObserver = ({ values, onValuesChange }) => {
  useEffect(() => {
    if (onValuesChange) onValuesChange(values);
  }, [onValuesChange, values]);

  return null;
};

const generateMonitorCode = (operatorCode = "") => {
  let code = "";
  let attempts = 0;
  do {
    code = String(generateSixDigitCode()).padStart(6, "0");
    attempts += 1;
  } while (code === String(operatorCode ?? "").trim() && attempts < 20);

  return code;
};

const getEventMode02Json = (json, operatorCode = "") => {
  const parsedJson = json && typeof json === "object" ? json : {};
  const credentialAccess = parsedJson.credentialAccess
    ? { ...parsedJson.credentialAccess }
    : parsedJson.credentialAccess;

  if (credentialAccess && (
    !/^\d{6}$/.test(String(credentialAccess.monitorCode ?? "")) ||
    String(credentialAccess.monitorCode) === String(operatorCode ?? "").trim()
  )) {
    credentialAccess.monitorCode = generateMonitorCode(operatorCode);
  } else if (credentialAccess && credentialAccess.monitorCode !== undefined) {
    credentialAccess.monitorCode = String(credentialAccess.monitorCode);
  }

  return {
    ...parsedJson,
    ...(credentialAccess ? { credentialAccess } : {}),
    server: {
      ...(parsedJson.server || {}),
      imageServer: parsedJson.server?.imageServer ?? domainConfig.imageServer ?? "",
    },
  };
};

function normalizeImportedFieldValue(rawValue, field) {
  if (field?.type === "checkbox" && field.allowMultipleChoice) {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => (item === null || item === undefined ? "" : String(item).trim()))
        .filter((item) => item !== "");
    }

    if (
      rawValue === undefined ||
      rawValue === null ||
      rawValue === "" ||
      rawValue === false ||
      rawValue === "false"
    ) {
      return [];
    }

    const parseAsArray = (input) => {
      try {
        const parsed = JSON.parse(input);
        if (Array.isArray(parsed)) return parsed;

        if (typeof parsed === "string") {
          const reparsed = JSON.parse(parsed);
          if (Array.isArray(reparsed)) return reparsed;
        }
      } catch (e) {
        return null;
      }

      return null;
    };

    let valueAsText = String(rawValue).trim();

    if (valueAsText.startsWith("'") && valueAsText.endsWith("'")) {
      valueAsText = valueAsText.slice(1, -1).trim();
    }

    const parsingCandidates = [valueAsText];
    if (valueAsText.startsWith('"') && valueAsText.endsWith('"')) {
      parsingCandidates.push(valueAsText.slice(1, -1).replace(/""/g, '"').trim());
    }

    for (const candidate of parsingCandidates) {
      const parsedArray = parseAsArray(candidate);
      if (Array.isArray(parsedArray)) {
        return parsedArray
          .map((item) => (item === null || item === undefined ? "" : String(item).trim()))
          .filter((item) => item !== "");
      }
    }

    if (valueAsText.startsWith("[") && valueAsText.endsWith("]")) {
      return valueAsText
        .slice(1, -1)
        .split(",")
        .map((item) => item.trim().replace(/^['\"]|['\"]$/g, ""))
        .filter((item) => item !== "");
    }

    return [valueAsText];
  }

  if ((field?.rfigtag === true || field?.validation === "rfid") && typeof rawValue === "string" && rawValue.trim() !== "") {
    return normalizeRfidTag(rawValue);
  }

  return rawValue;
}

export const FormComponentMode02 = ({
  fonts,
  dataTable,
  dataAddForm,
  setDataAddForm,
  modal,
  setModal,
  onValuesChange,
}) => {
  const dispatch = useAppDispatch();
  const notyf = useContext(NotyfContext);
  const { setLoading } = useLayout();
  const [activeTab, setActiveTab] = useState("form");
  const [errorNavigation, setErrorNavigation] = useState(null);
  const normalizedEventJson = useMemo(
    () => getEventMode02Json(dataTable.json, dataTable.codigo_credenciamento),
    [dataTable.json, dataTable.codigo_credenciamento]
  );
  const persistedSections = Array.isArray(dataTable.json?.sections)
    && dataTable.json.sections.length > 0
    ? dataTable.json.sections
    : {};
  const persistedForm = dataTable.json?.form || {};
  const persistedFields = useMemo(
    () => hydrateFormFieldVacancies(
      persistedForm.fields || [],
      dataTable.form_field_vacancies,
    ),
    [dataTable.form_field_vacancies, persistedForm.fields],
  );
  const persistedSocialMedia = dataTable.json?.layout?.footer?.socialMedia || [];
  const persistedZplModels = dataTable.json?.credentialAccess?.zplModels || [];
  const hasUnsavedPreviewData =
    JSON.stringify(dataAddForm.tabs ?? {}) !== JSON.stringify(persistedSections)
    || JSON.stringify(dataAddForm.fields ?? []) !== JSON.stringify(persistedFields)
    || JSON.stringify(dataAddForm.fieldsUsers ?? []) !== JSON.stringify(persistedForm.fieldsUsers ?? [])
    || JSON.stringify(dataAddForm.fieldsSympla ?? []) !== JSON.stringify(persistedForm.fieldsSympla ?? [])
    || JSON.stringify(dataAddForm.fieldsToShow ?? []) !== JSON.stringify(persistedForm.fieldsToShow ?? [])
    || JSON.stringify(dataAddForm.socialMedia ?? []) !== JSON.stringify(persistedSocialMedia)
    || JSON.stringify(dataAddForm.zplModels ?? []) !== JSON.stringify(persistedZplModels)
    || JSON.stringify(dataAddForm.activities ?? []) !== JSON.stringify(dataTable.activities ?? [])
    || JSON.stringify(dataAddForm.exhibitors ?? []) !== JSON.stringify(dataTable.exhibitors ?? []);

  function handlePreviewError(error) {
    notyf.open({
      type: "danger",
      message: error,
      ripple: true,
      dismissible: true,
    });
  }

  const navigateToValidationError = (validationErrors) => {
    const errorPaths = getErrorPaths(validationErrors);
    if (errorPaths.length === 0) return false;

    notyf.open({
      type: "danger",
      message: "Existem campos inválidos no formulário",
      ripple: true,
      dismissible: true,
    });

    if (!scrollToFirstError(errorPaths)) {
      const errorPath = errorPaths[0];
      const targetTab = errorPath.startsWith("json.credentialAccess")
        ? "credentialAccess"
        : errorPath.startsWith("json.sympla")
          ? "sympla"
          : errorPath.startsWith("json.form")
            ? "form"
            : errorPath.startsWith("json.server")
              ? "advanced"
              : "all";

      setErrorNavigation({ path: errorPath, requestId: Date.now() });
      setActiveTab(targetTab);

      const retryScroll = (remainingAttempts) => {
        window.setTimeout(() => {
          if (!scrollToFirstError(errorPaths) && remainingAttempts > 1) {
            retryScroll(remainingAttempts - 1);
          }
        }, 150);
      };

      retryScroll(4);
    }

    return true;
  };

  // Handle custom submit from side panel
  const handleCustomSubmit = async (applyOnly, values, actions) => {
    // Use the same validation logic as the form submit
    const { validateForm } = actions;
    const validationErrors = await validateForm();

    if (navigateToValidationError(validationErrors)) return;

    // Only call updateEvent02 if validation passes
    updateEvent02(
      values,
      actions,
      dataAddForm,
      setDataAddForm,
      dataTable,
      setLoading,
      notyf,
      dispatch,
      applyOnly,
    );
  };

  return (
    <Formik
      validate={(values) => {
        // Define initialValues for schema
        const initialValues = {
          event_id: dataTable.event_id,
          user_id: dataTable.user_id,
          title: dataTable.title,
          description: dataTable.description,
          json: normalizedEventJson,
          status: dataTable.status,
          creation_date: dataTable.data_criacao,
          end_date: dataTable.data_termino,
          token: dataTable.token,
          codigo_credenciamento: dataTable.codigo_credenciamento,
          emails: "",
          emailsExhibitors: "",
          selectedUserType: "1",
          emailsParticipants: "",
          activities: dataTable.activities,
          exhibitors: dataTable.exhibitors,
        };

        // Get the schema function result
        const validationSchema = schemaEventMode02(
          values,
          initialValues,
          dataTable.total_participantes,
        );

        let errors = {};
        try {
          validateYupSchema(values, validationSchema, true, {});
        } catch (err) {
          errors = yupToFormErrors(err);
        }

        // Additional custom validations based on business logic
        if (
          values.json.sympla?.status &&
          values.json.sympla?.selectedEventId &&
          dataAddForm.fieldsSympla.length > 0
        ) {
          dataAddForm.fieldsSympla.forEach((symplaField) => {
            const hasMapping = dataAddForm.fields.some(
              (field) => field._id === symplaField.name,
            );

            if (!hasMapping) {
              errors = {
                ...errors,
                json: {
                  ...errors.json,
                  sympla: {
                    ...errors.json?.sympla,
                    fieldMappings: {
                      ...errors.json?.sympla?.fieldMappings,
                      [symplaField.name]: "Campo obrigatório",
                    },
                  },
                },
              };
            }
          });
        }
        return errors;
      }}
      enableReinitialize
      onSubmit={(values, actions, event) => {
        // Check if the "Aplicar Alterações" button was clicked
        const applyOnly = event?.nativeEvent?.submitter?.name === "applyOnly";
        updateEvent02(
          values,
          actions,
          dataAddForm,
          setDataAddForm,
          dataTable,
          setLoading,
          notyf,
          dispatch,
          applyOnly,
        );
      }}
      initialValues={{
        event_id: dataTable.event_id,
        user_id: dataTable.user_id,
        title: dataTable.title,
        description: dataTable.description,
        json: normalizedEventJson,
        status: dataTable.status,
        creation_date: dataTable.data_criacao,
        end_date: dataTable.data_termino,
        token: dataTable.token,
        codigo_credenciamento: dataTable.codigo_credenciamento,
        emails: "",
        emailsExhibitors: "",
        selectedUserType: "1",
        emailsParticipants: "",
        activities: dataTable.activities,
        exhibitors: dataTable.exhibitors,
      }}
    >
      {({
        handleChange,
        validateForm,
        values,
        touched,
        errors,
        dirty,
        isSubmitting,
        setFieldValue,
        setSubmitting,
      }) => (
        <>
          <FormikValuesObserver values={values} onValuesChange={onValuesChange} />
          {(modal.showModalForm || modal.showModalFormUser) && (
            <FormType
              platform={dataTable.platform}
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
              user={modal.showModalFormUser ? true : false}
              formData={values}
              setEventFieldValue={setFieldValue}
            />
          )}
          {modal.showSocialMediaForm && (
            <SocialMediaType
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
            />
          )}
          {modal.showExtraSectionsForm && (
            <ExtraSectionsForm
              dataTable={dataTable}
              platform={dataTable.platform}
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
              handlePreviewError={handlePreviewError}
            />
          )}
          {modal.showZPLModelForm && (
            <ZPLModelForm
              dataTable={dataTable}
              platform={dataTable.platform}
              dataAddForm={dataAddForm}
              setDataAddForm={setDataAddForm}
              modal={modal}
              setModal={setModal}
            />
          )}
          <EmailLabelModal
            modal={modal}
            setModal={setModal}
            values={values}
            setFieldValue={setFieldValue}
          />
          <Form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleCustomSubmit(
                event.nativeEvent?.submitter?.name === "applyOnly",
                values,
                { setSubmitting, validateForm },
              );
            }}
          >
            <div className="tab mt-3">
              <SideButtonPanel
                dataTable={dataTable}
                hasUnsavedChanges={dirty || hasUnsavedPreviewData}
                isSubmitting={isSubmitting}
                onSubmit={(applyOnly) =>
                  handleCustomSubmit(applyOnly, values, {
                    setSubmitting,
                    validateForm,
                  })
                }
              ></SideButtonPanel>
              <EventDefinitions
                dataTable={dataTable}
                values={values}
                touched={touched}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                setDataAddForm={setDataAddForm}
                dataAddForm={dataAddForm}
                setModal={setModal}
                setActiveTab={setActiveTab}
              ></EventDefinitions>
              <Tab.Container
                id="left-tabs-example"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
              >
                <Nav variant="tabs" className="">
                  <Nav.Item>
                    <Nav.Link eventKey={"form"} style={{
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <FontAwesomeIcon icon={faFileText} className="me-1" /> Definições de registro
                    </Nav.Link>
                  </Nav.Item>
                  {values.json.networking?.status && (
                    <Nav.Item>
                      <Nav.Link eventKey={"all"} style={{
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center"
                      }}>
                        <FontAwesomeIcon icon={faReact} className="me-1" /> Networking
                      </Nav.Link>
                    </Nav.Item>
                  )}
                  {values.json.credentialAccess.status && (
                    <Nav.Item>
                      <Nav.Link eventKey={"credentialAccess"} style={{
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center"
                      }}>
                        <FontAwesomeIcon icon={faReact} className="me-1" /> Credenciamento
                      </Nav.Link>
                    </Nav.Item>
                  )}

                  {values.json.sympla.status && (
                    <Nav.Item style={{
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <Nav.Link eventKey={"sympla"}><FontAwesomeIcon icon={faNetworkWired} className="me-1" /> Sympla</Nav.Link>
                    </Nav.Item>
                  )}
                  <Nav.Item style={{
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center"
                  }}>
                    <Nav.Link eventKey={"advanced"}><FontAwesomeIcon icon={faCog} className="me-1" /> Configurações</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey={"form"} className="mt-3">
                    <RegisterDefinitions
                      dataTable={dataTable}
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      setDataAddForm={setDataAddForm}
                      dataAddForm={dataAddForm}
                      setModal={setModal}
                    ></RegisterDefinitions>
                  </Tab.Pane>
                  {values.json.networking?.status && (
                    <Tab.Pane eventKey={"all"} className="mt-3">
                      <AppDefinitions
                        dataTable={dataTable}
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        setDataAddForm={setDataAddForm}
                        dataAddForm={dataAddForm}
                        handlePreviewError={handlePreviewError}
                        fonts={fonts}
                        setModal={setModal}
                        errorNavigation={errorNavigation}
                      ></AppDefinitions>
                    </Tab.Pane>
                  )}
                  {values.json.credentialAccess?.status && (
                    <Tab.Pane eventKey={"credentialAccess"} className="mt-3">
                      <CredentialsComponent
                        dataTable={dataTable}
                        values={values}
                        notyf={notyf}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setModal={setModal}
                        setFieldValue={setFieldValue}
                        setDataAddForm={setDataAddForm}
                        dataAddForm={dataAddForm}
                        handlePreviewError={handlePreviewError}
                        fonts={fonts}
                        errorNavigation={errorNavigation}
                      ></CredentialsComponent>
                    </Tab.Pane>
                  )}

                  {values.json.sympla?.status && (
                    <Tab.Pane eventKey={"sympla"} className="mt-3">
                      <SymplaComponent
                        dataTable={dataTable}
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        setDataAddForm={setDataAddForm}
                        dataAddForm={dataAddForm}
                      ></SymplaComponent>
                    </Tab.Pane>
                  )}
                  <Tab.Pane eventKey={"advanced"} className="mt-3">
                    <AdvancedSettingsDefinitions
                      dataTable={dataTable}
                      values={values}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                      onDelete={() =>
                        handleDelete(
                          dataTable,
                          dataTable.platform,
                          setLoading,
                          notyf,
                          dispatch,
                          eventList,
                        )
                      }
                    ></AdvancedSettingsDefinitions>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};

export async function updateEvent02(
  values,
  actions,
  dataForm,
  setDataAddForm,
  dataTable,
  setLoading,
  notyf,
  dispatch,
  applyOnly = false,
) {
  if (dataForm.importUsers.error) {
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: "Corrija os erros da importação antes de salvar o evento.",
      ripple: true,
      dismissible: true,
    });
    return;
  }
  setLoading(true);
  const updatedValues = JSON.parse(JSON.stringify(values));

  if (updatedValues.json?.email?.register) {
    updatedValues.json.email.register = normalizeRegisterTemplateConfiguration(
      updatedValues.json.email.register,
    );
  }

  const registerConfig = updatedValues.json?.email?.register;
  const missingTemplateType = REGISTRATION_EMAIL_USER_TYPES.find(
    ({ type }) => !hasRegisterTemplateForType(registerConfig, type),
  );

  if (registerConfig?.status && missingTemplateType) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: "Selecione um modelo de e-mail para cada tipo de usuário antes de salvar com o envio automático habilitado.",
      ripple: true,
      dismissible: true,
    });
    return;
  }

  if (dataForm.fieldsToShow.length <= 0) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: `Identifique ao menos 1 dos campos dos formulário como "Campo de identificação"`,
      ripple: true,
      dismissible: true,
    });
    return; // Exit the function immediately
  }
  if (dataForm.fields.length <= 0) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: `Adicione ao menos 1 campo na "Composição do formulário dos participante"`,
      ripple: true,
      dismissible: true,
    });
    return; // Exit the function immediately
  }
  if (dataForm.fieldsUsers.length <= 0) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: `Adicione ao menos 1 campo na "Composição do formulário dos usuários gestores"`,
      ripple: true,
      dismissible: true,
    });
    return; // Exit the function immediately
  }
  updatedValues.json.form.fields = dataForm.fields;
  updatedValues.json.form.fieldsUsers = dataForm.fieldsUsers.map((field) => ({
    ...field,
    rfigtag: false,
  }));
  const whatsappField = getWhatsAppRegistrationField(updatedValues.json.form.fields, updatedValues.json.form.fieldsUsers,
    updatedValues.json.whatsapp?.register);
  if (updatedValues.json.whatsapp?.register?.status) {
    const whatsappRegister = updatedValues.json.whatsapp.register;
    const enabledTypes = WHATSAPP_REGISTRATION_USER_TYPES.filter(({ type }) => whatsappRegister.templates?.[String(type)]?.enabled !== false);
    if (!whatsappField.field || !whatsappRegister.sender_id || !enabledTypes.length
      || enabledTypes.some(({ type }) => !whatsappRegister.templates?.[String(type)]?.template_id
        || !whatsappRegister.templates?.[String(type)]?.template_version)) {
      setLoading(false); actions.setSubmitting(false);
      notyf.open({ type: "danger", message: whatsappField.error || "Selecione o remetente e um modelo de WhatsApp para cada tipo habilitado." });
      return;
    }
  }
  updatedValues.json.form.fieldsToShow = dataForm.fieldsToShow;
  updatedValues.json.form.fieldsUep = dataForm.fieldsUep;

  const hasRfidField = dataForm.fields.some((field) => field?.rfigtag === true);
  updatedValues.json.credentialAccess.apresentation.afterCheckin.rfid = {
    ...updatedValues.json.credentialAccess.apresentation.afterCheckin.rfid,
    status: hasRfidField,
  };

  updatedValues.json.form.fieldsSympla = dataForm.fieldsSympla;
  updatedValues.json.layout.footer.socialMedia = dataForm.socialMedia;
  updatedValues.json.credentialAccess.zplModels = dataForm.zplModels;
  updatedValues.json.sections = dataForm.tabs;

  let formattedDataVisitantes = [];
  let formattedDataUsuarios = [];
  let formattedDataVisitantesForm = [];
  const formattedManagerEmails =
    updatedValues.emails !== "" ? textToArray(updatedValues.emails) : [];
  const formattedExhibitorEmails =
    updatedValues.emailsExhibitors !== ""
      ? textToArray(updatedValues.emailsExhibitors)
      : [];
  const formattedEmailsParticipants =
    updatedValues.emailsParticipants !== ""
      ? textToArray(updatedValues.emailsParticipants)
      : [];

  const normalizeEmailList = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((email) => typeof email === "string" && email.trim() !== "")
      .map((email) => email.trim().toLowerCase());

  const managerEmailSet = new Set(normalizeEmailList(formattedManagerEmails));
  const exhibitorEmailSet = new Set(normalizeEmailList(formattedExhibitorEmails));
  const participantEmailSet = new Set([
    ...normalizeEmailList(formattedEmailsParticipants),
    ...normalizeEmailList((dataForm?.importUsers?.users || []).map((row) => row?.email || row?.Email)),
  ]);

  const hasCrossTypeDuplicates =
    [...managerEmailSet].some((email) => exhibitorEmailSet.has(email) || participantEmailSet.has(email))
    || [...exhibitorEmailSet].some((email) => participantEmailSet.has(email));

  if (hasCrossTypeDuplicates) {
    setLoading(false);
    actions.setSubmitting(false);
    notyf.open({
      type: "danger",
      message: 'Existem e-mails duplicados em "Adicionar registros" para diferentes tipos de usuários',
      ripple: true,
      dismissible: true,
    });
    return;
  }

  // Validate manager and exhibitor emails (textarea)
  const userEmailGroups = [
    { emails: formattedManagerEmails, type: 1, label: "Usuário gestor" },
    { emails: formattedExhibitorEmails, type: 2, label: "Expositor" },
  ];
  for (const userEmailGroup of userEmailGroups) {
    for (const email of userEmailGroup.emails) {
      const validation = validateEmailDomain(email, updatedValues);
      if (!validation.valid) {
        setLoading(false);
        actions.setSubmitting(false);
        const message =
          validation.error === "formato"
            ? `${userEmailGroup.label} com email inválido: ${email}`
            : `${userEmailGroup.label} com domínio não permitido: ${email}. Domínios permitidos: ${validation.allowedDomains}`;
        notyf.open({
          type: "danger",
          message: message,
          ripple: true,
          dismissible: true,
        });
        return;
      }
      formattedDataUsuarios.push({ email, type: userEmailGroup.type });
    }
  }

  // Validate participant emails (textarea)
  if (formattedEmailsParticipants.length > 0) {
    for (const email of formattedEmailsParticipants) {
      const validation = validateEmailDomain(email, updatedValues);
      if (!validation.valid) {
        setLoading(false);
        actions.setSubmitting(false);
        const message =
          validation.error === "formato"
            ? `Participante com email inválido: ${email}`
            : `Participante com domínio não permitido: ${email}. Domínios permitidos: ${validation.allowedDomains}`;
        notyf.open({
          type: "danger",
          message: message,
          ripple: true,
          dismissible: true,
        });
        return;
      }
      formattedDataVisitantes.push(email);
    }
  }

  // All validation already ran at file-pick time in handleFileUpload.
  // Here we just reshape each row into the { inputID: { label, value } } format
  // expected by the API (formattedDataVisitantesForm).
  if (dataForm.importUsers.users.length > 0) {
    for (const row of dataForm.importUsers.users) {
      const rowObject = {};
      const valuesByField = updatedValues.json.form.fields.reduce((result, field) => {
        result[field.inputID] = normalizeImportedFieldValue(row[field.label], field);
        return result;
      }, {});
      for (const key of Object.keys(row)) {
        const matchedField = updatedValues.json.form.fields.find(
          (field) => field.label === key,
        );
        if (matchedField && isFieldVisible(matchedField, updatedValues.json.form.fields, valuesByField)) {
          const normalizedValue = normalizeImportedFieldValue(row[key], matchedField);
          rowObject[matchedField.inputID] = {
            label: key,
            value: normalizedValue,
          };
        } else if (key === "email") {
          rowObject.email = row[key];
        }
      }
      rowObject.__importRow = row.__importRow;
      formattedDataVisitantesForm.push(rowObject);
    }
  }

  // Parse the date assuming it's in Brasília timezone (GMT-3)
  const formattedDate = parse(
    updatedValues.json.form.register_start_date,
    "dd/MM/yyyy HH:mm",
    new Date(),
  );
  // Convert from Brasília time to UTC (this will add 3 hours)
  const startDateUTC = zonedTimeToUtc(formattedDate, "America/Sao_Paulo");
  updatedValues.json.form.register_start_date = formatInTimeZone(
    startDateUTC,
    "UTC",
    "yyyy-MM-dd HH:mm",
  );

  const formattedDate2 = parse(
    updatedValues.json.form.register_end_date,
    "dd/MM/yyyy HH:mm",
    new Date(),
  );
  // Convert from Brasília time to UTC (this will add 3 hours)
  const endDateUTC = zonedTimeToUtc(formattedDate2, "America/Sao_Paulo");
  updatedValues.json.form.register_end_date = formatInTimeZone(
    endDateUTC,
    "UTC",
    "yyyy-MM-dd HH:mm",
  );

  axios
    .put(`/api/event/${dataTable.evento_id}`, {
      user_id: values.user_id,
      title: updatedValues.title,
      description: updatedValues.description,
      status: values.status == 0 ? 2 : values.status,
      json: JSON.stringify(updatedValues.json),
      end_date: values.end_date,
      codigo_credenciamento: values.codigo_credenciamento,
      platform: dataTable.platform,
      addVisitantes: JSON.stringify(formattedDataVisitantes),
      addVisitantesForm: JSON.stringify(formattedDataVisitantesForm),
      addUsers: JSON.stringify(formattedDataUsuarios),
      sendEmail: updatedValues.json.email.register.status ? 1 : 0,
      sendWhatsapp: updatedValues.json.whatsapp?.register?.status ? 1 : 0,
      disableValidation: dataForm.importUsers.disableValidation ? 1 : 0,
      activeUserAutomatically: dataForm.importUsers.activeUserAutomatically !== false ? 1 : 0,
    })
    .then(function (response) {
      setLoading(false);
      if (response.status === 201) {
        notyf.open({
          type: "success",
          message: applyOnly
            ? "Alterações aplicadas com sucesso"
            : "Evento salvo com sucesso",
          ripple: true,
          dismissible: true,
        });
        if (!applyOnly) {
          dispatch(eventList());
        } else {
          // Refetch the data to ensure state security and consistency
          dispatch(eventList({ fetch: true, keepOpen: true }));
        }
      } else {
        actions.setSubmitting(false);
        notyf.open({
          type: "danger",
          message: "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      }
    })
    .catch(function (error) {
      setLoading(false);
      actions.setSubmitting(false);
      const importErrors = error?.response?.data?.errors;
      const errors = Array.isArray(importErrors)
        ? importErrors.map((item) => normalizeServerImportValidationError(
          item,
          dataForm.importUsers.users[Number(item.row_index)]?.__importRow || null,
        ))
        : [];
      if (errors.length > 0) {
        const summary = getImportValidationSummary(errors);
        setDataAddForm((previous) => ({
          ...previous,
          importUsers: {
            ...previous.importUsers,
            error: summary,
            errorList: errors,
            report: createImportValidationReport(errors, summary),
          },
        }));
      }
      notyf.open({
        type: "danger",
        message: String(error?.response?.data?.code || "").startsWith("WHATSAPP_")
          ? getWhatsAppRegistrationError(error.response.data.code)
          : getImportValidationToast(errors, error?.response?.data?.code || "EVENT_UPDATE_FAILED"),
        ripple: true,
        dismissible: true,
      });
    });
}
